import { HelpBlock, HelpGuide, HelpLanguage, HelpResource, HelpResourceKind, HelpTopic } from "./help.types";

const VIDEO_EXTENSION = "webm";
const DEFAULT_IMAGE_EXTENSION = "jpg";

/** Unique resource id: `<screenCode>.<sectionId>.<key>.<lang>`. */
export function buildHelpResourceId(screenCode: string, sectionId: string, key: string, lang: HelpLanguage): string {
  return [screenCode, sectionId, key, lang].join(".");
}

/** Splits an image file name into base name and extension (defaults to jpg). */
function splitFileName(file: string): { key: string; ext: string } {
  const dot = file.lastIndexOf(".");
  return dot > 0
    ? { key: file.slice(0, dot), ext: file.slice(dot + 1).toLowerCase() }
    : { key: file, ext: DEFAULT_IMAGE_EXTENSION };
}

/** Resource row of an image/video block; null for other blocks. */
export function helpBlockResource(
  topic: Pick<HelpTopic, "id" | "code">,
  sectionId: string,
  block: HelpBlock,
  lang: HelpLanguage
): HelpResource | null {
  let key: string;
  let ext: string;
  let kind: HelpResourceKind;
  if (block.kind === "image") {
    ({ key, ext } = splitFileName(block.src));
    kind = "image";
  } else if (block.kind === "video") {
    key = block.mediaId;
    ext = VIDEO_EXTENSION;
    kind = "video";
  } else {
    return null;
  }
  const id = buildHelpResourceId(topic.code, sectionId, key, lang);
  return {
    id,
    screenCode: topic.code,
    topicId: topic.id,
    sectionId,
    key,
    kind,
    lang,
    folder: topic.code,
    fileName: `${id}.${ext}`
  };
}

/** Resources table of one guide: one row per image/video block. */
export function buildHelpResources(topic: HelpTopic, guide: HelpGuide): HelpResource[] {
  return guide.sections.flatMap(section =>
    section.blocks.flatMap(block => helpBlockResource(topic, section.id, block, guide.lang) ?? [])
  );
}

/** Bundled asset URL: `assets/help/<screenCode>/<resourceId>.<ext>`, same layout as the backend. */
export function helpResourceUrl(resource: Pick<HelpResource, "folder" | "fileName">): string {
  return `assets/help/${helpResourcePath(resource)}`;
}
/** Backend path of a resource file: `<folder>/<fileName>`. */
export function helpResourcePath(resource: Pick<HelpResource, "folder" | "fileName">): string {
  return `${resource.folder}/${resource.fileName}`;
}

/** Index of resources by id. */
export function indexHelpResources(resources: ReadonlyArray<HelpResource>): Map<string, HelpResource> {
  return new Map(resources.map(r => [r.id, r]));
}

/** Ids that appear more than once. */
export function findDuplicateHelpResourceIds(resources: ReadonlyArray<HelpResource>): string[] {
  const seen = new Set<string>();
  const duplicates = new Set<string>();
  for (const { id } of resources) {
    if (seen.has(id)) {
      duplicates.add(id);
    }
    seen.add(id);
  }
  return [...duplicates];
}
